import React, { ChangeEvent, memo } from "react";
import { Box, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { FormikProps, useFormikContext } from "formik";
import { TerminalApp, Data } from "../../../../../store/terminal/types";
import SelectDropDown from "../../../../../common/formElements/SelectDropDown";
import AutocompleteField from "../../../../../common/formElements/AutocompleteField";
import { isNil } from "ramda";
import { uniq } from "lodash";
import { ActiveProvider } from "../../../../../lib/utils";

interface ApplicationForm {
  index: number;
  application: TerminalApp;
  applicationOptions: Array<string>;
  versions: Record<string, { applicationData?: Array<{ versionCode: string; variant: string }> }> | null | undefined;
  isUpdateAllowed?: boolean;
}

const ApplicationForm = (props: ApplicationForm) => {
  const { index, application, applicationOptions, versions, isUpdateAllowed } =
    props;
  const formikProps = useFormikContext();
  const { values, setFieldValue } = formikProps as FormikProps<Data>;
  
  // Early return if critical data is missing
  if (!application || !Array.isArray(applicationOptions)) {
    return null;
  }
  
  const appName = application.AppName;
  const currentApps = (values.attributes?.Applications || []).map(
    (i: { AppName: string }) => i.AppName
  ).filter(Boolean);

  const manufacturer = values.attributes?.Manufacturer || "";
  const isQuest = manufacturer?.toUpperCase() === "QUEST";
  const isDMG = process.env.IDP === "Auth0" ? true : false;

  const applicationList = !isQuest
    ? (Array.isArray(applicationOptions) ? applicationOptions : [])
    : (Array.isArray(applicationOptions) ? applicationOptions : []).filter(
        (item) => item === "NAB Payment App" || item === "DMG Updater"
      ) || [];

  const onChangeVersion = (_event: React.ChangeEvent<{}>, value: string) => {
    if (!isNil(value) && application.AppName && versions && versions[application.AppName] && Array.isArray(versions[application.AppName].applicationData)) {
      const version = versions[application.AppName];
      setFieldValue(`attributes.Applications[${index}].VersionString`, value);
      const versionData = version.applicationData.find(
        (i: { versionCode: string; variant: string }) =>
          i.versionCode === value &&
          i.variant?.toLocaleUpperCase() ===
            manufacturer?.toLocaleUpperCase()
      );
      if (versionData && versionData.variant) {
        setFieldValue(
          `attributes.Applications[${index}].Variant`,
          versionData.variant
        );
      }
    }
  };

  const onChangeApplication = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFieldValue(name, value);
    return;
  };

  const isAppNameDisabled = Boolean(application.AppName);
  let versionOptions: string[] = [];
  
  if (application.AppName && versions && versions[application.AppName] && Array.isArray(versions[application.AppName].applicationData)) {
    versionOptions = uniq(
      versions[application.AppName].applicationData
        // Make sure we pick the correct App version.
        .filter(
          (i) =>
            i.variant?.toUpperCase() ===
            manufacturer?.toUpperCase()
        )
        .map((i) => i.versionCode)
    );
  }

  if (isDMG && isQuest && Array.isArray(versionOptions)) {
    versionOptions = versionOptions.filter((i: string) => {
      if(i == "1.7.8"){ //condition for allowing v1.7.5 version
        return parseFloat(i)
      }
      return parseFloat(i) < 1.4;
    });
  }

  // Ensure versionOptions is always an array
  if (!Array.isArray(versionOptions)) {
    versionOptions = [];
  }

  return (
    <Grid container alignItems="flex-start" spacing={2}>
      <Grid item xs={12} md={4}>
        {Boolean(applicationList && applicationList.length) && (
          <SelectDropDown
            displayErr={true}
            fieldname={`attributes.Applications[${index}].AppName`}
            label="Select Application"
            disabled={isAppNameDisabled || !isUpdateAllowed}
            options={
              Array.isArray(applicationList) && Array.isArray(currentApps)
                ? applicationList.filter(
                    (app: string) =>
                      !currentApps.includes(app) || isAppNameDisabled
                  )
                : []
            }
            onChange={onChangeApplication}
          />
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        {appName && application?.isEditAble ? (
          <AutocompleteField
            fieldname={`attributes.Applications[${index}].VersionString`}
            value={application.VersionString}
            disableClearable={true}
            options={versionOptions}
            onChange={onChangeVersion}
            renderInput={(params) => (
              <TextField variant="outlined" {...params} label="Version" />
            )}
            disabled={!isUpdateAllowed}
          />
        ) : (
          <>
            <InputLabel shrink>Version</InputLabel>
            <Box>
              <Typography>{application.VersionString}</Typography>
            </Box>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <InputLabel shrink>Hardware</InputLabel>
        <Box>
          <Typography>{application.Variant}</Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default memo(ApplicationForm);
